import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { collabStateManager } from '@databases/collab-state-manager';
import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { installPerfTracerForDatabase } from '@databases/frontend-metrics/perf-tracing';
import { perf } from '@databases/logs/performance';
import {
  buildDatabaseFromCollabAndContent,
  syncCollabChangesToRedux,
} from '@databases/redux/collab/actions';
import { appStore } from '@databases/redux/store';
import { type ConnectedDatabase } from '@databases/sdk-loader/connected-database';
import {
  addDatabaseToList,
  fakeDatabaseContent,
  markDatabaseAsLoaded,
} from '@databases/utils/redux/databases/actions';

const DATABASE_CONTENT_TYPE = 'database' as const;

/**
 * Resolves a pending SDK loader promise, converts the result into the
 * Redux `Database` shape, injects it directly into the store (skipping
 * the PENDING loading state), and wires the collab manager for ongoing
 * diff sync and mutation perf tracing.
 *
 * Returns `true` if the SDK load succeeded, `false` if it failed (in
 * which case the caller should fall back to the legacy Redux thunk path).
 */
export async function resolveSdkDatabaseLoad(
  sdkLoadPromise: Promise<ConnectedDatabase | null>,
  {
    contentId,
    contentReferenceId,
    spaceId,
    spaceKey,
    spaceName,
    title,
  }: {
    contentId: string;
    contentReferenceId: string;
    spaceId: string;
    spaceKey: string;
    spaceName: string;
    title: string;
  }
): Promise<boolean> {
  try {
    const connectedDb = await sdkLoadPromise;

    if (!connectedDb) {
      return false;
    }

    perf.timeLog('app-initialization', 'sdk-database-load-end');
    GlobalPageLoadExperience.mark('sdk-database-load-end');

    const database = buildDatabaseFromCollabAndContent(
      {
        id: contentId,
        referenceId: contentReferenceId,
        space: { id: Number(spaceId), key: spaceKey, name: spaceName },
        type: DATABASE_CONTENT_TYPE,
        title,
        ...fakeDatabaseContent,
      },
      connectedDb.yDatabase,
      connectedDb.isReadOnly
    );

    appStore.dispatch(addDatabaseToList(database));
    appStore.dispatch(markDatabaseAsLoaded(contentId));

    const manager = connectedDb.manager as CollabManager;
    collabStateManager.setCollabManager(contentId, manager);

    connectedDb.yDatabase.doc.on(
      'update',
      syncCollabChangesToRedux(manager, appStore.dispatch, {
        contentId,
        isReadOnly: connectedDb.isReadOnly,
      })
    );

    installPerfTracerForDatabase(manager, contentId);

    return true;
  } catch {
    return false;
  }
}
